import type { StatusMode } from '../lib/types';

const sizeMap: Record<string, string> = {
  xs: 'h-7 w-7 text-[11px]',
  sm: 'h-9 w-9 text-xs',
  md: 'h-11 w-11 text-sm',
  lg: 'h-16 w-16 text-lg',
  xl: 'h-24 w-24 text-2xl',
};

// Presence dot scales with the avatar so it never looks oversized on small ones.
const dotSizeMap: Record<string, string> = {
  xs: 'h-2 w-2 ring-2',
  sm: 'h-2.5 w-2.5 ring-2',
  md: 'h-3 w-3 ring-2',
  lg: 'h-3.5 w-3.5 ring-[3px]',
  xl: 'h-4 w-4 ring-4',
};

const MODE_LABEL: Record<StatusMode, string> = {
  available: 'voľný',
  busy: 'zaneprázdnený',
  away: 'preč',
  focus: 'sústredenie',
  offline: 'offline',
};

const initials = (name: string): string =>
  name
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase() ?? '')
    .join('') || '?';

export const Avatar = ({
  name,
  src,
  size = 'md',
  statusMode,
  statusColor,
  online,
}: {
  name: string;
  src?: string | null;
  size?: keyof typeof sizeMap;
  statusMode?: StatusMode;
  /** Colour the person chose for their status; null/undefined = the mode's own. */
  statusColor?: string | null;
  online?: boolean;
}) => {
  // online === undefined -> presence unknown at this call site: show a dot only
  // for an explicit non-available status. online defined -> always show a dot;
  // offline wins over any stored statusMode.
  //
  // Picking "Offline" by hand is still only a hint here: the server keeps
  // reporting the person online while their heartbeat runs, and honouring the
  // choice in this dot alone made a single row claim both at once. Making it
  // real means changing the server's `online` for every list that groups by it.
  let mode: StatusMode | undefined;
  if (online === undefined) {
    mode =
      statusMode && statusMode !== 'available' && statusMode !== 'offline' ? statusMode : undefined;
  } else if (!online) {
    mode = 'offline';
  } else {
    mode = statusMode && statusMode !== 'offline' ? statusMode : 'available';
  }

  // Outer wrapper is positioned but NOT clipped, so the presence dot can sit on
  // the avatar edge. The inner `.avatar` keeps overflow-hidden to round the image.
  return (
    // aspect-square and an absolutely positioned image: a portrait photo used
    // to define the box itself when the percentage height had nothing definite
    // to resolve against, and the avatar came out as an egg.
    <span className={`relative inline-grid aspect-square shrink-0 ${sizeMap[size]}`}>
      <span className="avatar absolute inset-0 h-full w-full">
        {src ? (
          <img src={src} alt={name} className="absolute inset-0 h-full w-full object-cover" />
        ) : (
          <span aria-hidden="true">{initials(name)}</span>
        )}
      </span>
      {mode ? (
        <span
          className={`presence-dot presence-${mode} ${dotSizeMap[size]}`}
          // A chosen colour paints the dot, but never when the person is
          // offline — offline is a fact about presence, not a mood, and it has
          // to stay the one grey everyone recognises.
          style={statusColor && mode !== 'offline' ? { background: statusColor } : undefined}
          role="img"
          aria-label={mode === 'offline' ? 'offline' : `online — ${MODE_LABEL[mode]}`}
        />
      ) : null}
    </span>
  );
};
